import { IonButton, IonContent, IonInput, IonPage, IonSpinner } from '@ionic/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import { fetchMe, login } from '~/api/auth';
import { type CodedError, readCodedError } from '~/api/codedError';
import type { OfficeStatus, Tenant } from '~/api/tenant';
import { OfficeLogo } from '~/components/OfficeLogo';
import { SuspendedNotice } from '~/components/SuspendedNotice';
import { useTenant } from '~/components/TenantGate';
import { GridPattern } from '~/components/ui/GridPattern';
import { Icon } from '~/components/ui/Icon';
import { BACKEND_ROUTES, backendUrl } from '~/config';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { ROUTES } from '~/routes';
import { clearLocalData } from '~/session/clearLocalData';
import { loginHandoffOf } from '~/session/loginHandoff';
import { hasUserChanged, rememberUserId } from '~/session/userSwitch';
import { useAuthStore } from '~/stores/auth';

interface LoginError {
  message?: string;
  detail?: string;
  non_field_errors?: string[];
  email?: string[];
  password?: string[];
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as LoginError | undefined;
    // Recusas por escritório (OFFICE_ACCESS_REQUIRED, OFFICE_SLUG_REQUIRED, OFFICE_SUSPENDED)
    // chegam como {code, message, details}; as demais como {detail} ou por campo.
    if (data?.message) return data.message;
    if (data?.detail) return data.detail;
    if (data?.non_field_errors?.[0]) return data.non_field_errors[0];
    if (data?.email?.[0]) return data.email[0];
    if (data?.password?.[0]) return data.password[0];
    if (error.response?.status === 401) return 'Email ou senha incorretos.';
    if (error.code === 'ERR_NETWORK') return 'Falha de conexão. Verifique sua internet.';
  }
  return 'Não foi possível entrar. Tente novamente.';
}

const LOGIN_REFUSAL = {
  officeSuspended: 'OFFICE_SUSPENDED',
  officeAccessRequired: 'OFFICE_ACCESS_REQUIRED',
} as const;

const WRONG_OFFICE_HINT = 'Confira se este é o endereço do seu escritório';

/** Mesmos textos do backend (`tenancy.access.OfficeSuspended`), para o aviso antes do envio. */
const NOTICE_BY_OFFICE_STATUS: Record<Exclude<OfficeStatus, 'ACTIVE'>, string> = {
  SUSPENDED: 'O escritório está suspenso. Fale com o seu escritório de contabilidade.',
  CLOSED: 'O escritório encerrou as atividades nesta plataforma.',
};

function refusalOf(error: unknown): CodedError | null {
  return error instanceof AxiosError ? readCodedError(error.response?.data) : null;
}

/**
 * Aviso de escritório fora de `ACTIVE`: a mensagem do backend quando ele recusou por isso e, antes
 * do envio, a do status do tenant. O aviso não bloqueia o envio: quem recusa é o backend.
 */
function suspendedMessageFor(tenant: Tenant, error: unknown): string | null {
  const refusal = refusalOf(error);
  if (refusal?.code === LOGIN_REFUSAL.officeSuspended && refusal.message) {
    return refusal.message;
  }
  return tenant.status === 'ACTIVE' ? null : NOTICE_BY_OFFICE_STATUS[tenant.status];
}

interface LoginFailure {
  message: string;
  hint: string | null;
}

/** Erro do envio fora do aviso de suspensão; `null` sem erro ou quando o aviso já o mostra. */
function loginFailureOf(error: unknown): LoginFailure | null {
  if (!error) return null;
  const refusal = refusalOf(error);
  if (refusal?.code === LOGIN_REFUSAL.officeSuspended && refusal.message) return null;
  if (refusal?.code === LOGIN_REFUSAL.officeAccessRequired) {
    return { message: refusal.message || extractErrorMessage(error), hint: WRONG_OFFICE_HINT };
  }
  return { message: extractErrorMessage(error), hint: null };
}

const FORGOT_PASSWORD_URL = backendUrl(BACKEND_ROUTES.forgotPassword);

export function Login(): JSX.Element {
  const tenant = useTenant();
  const accessToken = useAuthStore((state) => state.accessToken);
  const isHydrated = useAuthStore((state) => state.isHydrated);
  const setSession = useAuthStore((state) => state.setSession);
  const nav = useAppNavigation();
  const queryClient = useQueryClient();
  // Recado de quem mandou entrar (o aceite do convite): e-mail para preencher e aviso para mostrar.
  const handoff = loginHandoffOf(useLocation().state);

  const [email, setEmail] = useState(handoff?.email ?? '');
  const [password, setPassword] = useState('');

  const loginMutation = useMutation({
    mutationFn: async () => {
      const session = await login(email, password, tenant.slug);
      useAuthStore.getState().setSession({
        accessToken: session.access,
        refreshToken: session.refresh,
        user: session.user,
      });
      const me = await fetchMe();
      if (hasUserChanged(me.user.id)) {
        // Outro usuário neste navegador: nada do anterior pode sobreviver ao login.
        await clearLocalData(queryClient);
      }
      return { session, me };
    },
    onSuccess: ({ session, me }) => {
      setSession({
        accessToken: session.access,
        refreshToken: session.refresh,
        user: me.user,
        companies: me.companies,
      });
      rememberUserId(me.user.id);
      queryClient.clear();
      nav.replace(ROUTES.dashboard);
    },
  });

  if (isHydrated && accessToken) {
    return <Redirect to={ROUTES.dashboard} />;
  }

  const suspendedMessage = suspendedMessageFor(tenant, loginMutation.error);
  const failure = loginFailureOf(loginMutation.error);

  const submit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!email || !password || loginMutation.isPending) return;
    loginMutation.mutate();
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="rx-container rx-screen flex flex-col min-h-full">
          {/* Padrão de grid decorativo no canto — motivo cybersecure. */}
          <div className="relative px-8 pt-12 flex-1 flex flex-col">
            <GridPattern
              size={120}
              step={10}
              opacity={0.04}
              style={{ position: 'absolute', top: 56, right: -10 }}
            />

            <div style={{ marginTop: 40 }}>
              <OfficeLogo tenant={tenant} />
            </div>

            <div style={{ marginTop: 96 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--rx-text-3)',
                  letterSpacing: '0.8px',
                  textTransform: 'uppercase',
                }}
              >
                Bem-vindo de volta
              </div>
              <h1
                style={{
                  margin: '10px 0 0',
                  fontSize: 32,
                  fontWeight: 500,
                  color: 'var(--rx-text)',
                  letterSpacing: '-0.8px',
                  lineHeight: 1.1,
                }}
              >
                Entre na sua conta
              </h1>
              <p
                style={{
                  marginTop: 12,
                  fontSize: 14,
                  color: 'var(--rx-text-2)',
                  lineHeight: 1.45,
                  maxWidth: '32ch',
                }}
              >
                Emita e acompanhe suas notas fiscais de serviço com segurança.
              </p>
            </div>

            {/* Pill com o nome do escritório do endereço e indicador online. */}
            <div
              style={{
                marginTop: 24,
                display: 'inline-flex',
                alignSelf: 'flex-start',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: 'var(--rx-surface)',
                border: '0.5px solid var(--rx-border)',
                borderRadius: 999,
              }}
            >
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 6,
                  background: 'var(--rx-positive)',
                }}
              />
              <span
                className="rx-mono"
                style={{ fontSize: 11, color: 'var(--rx-text-2)', fontWeight: 500 }}
              >
                {tenant.nome}
              </span>
            </div>

            {handoff?.notice && (
              <output
                style={{
                  marginTop: 16,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '10px 12px',
                  background: 'var(--rx-positive-tint)',
                  border: '0.5px solid var(--rx-positive)',
                  borderRadius: 10,
                  fontSize: 13,
                  color: 'var(--rx-text)',
                }}
              >
                <Icon name="check" size={16} color="var(--rx-positive)" />
                <span>{handoff.notice}</span>
              </output>
            )}

            {suspendedMessage && (
              <div style={{ marginTop: 16 }}>
                <SuspendedNotice
                  message={suspendedMessage}
                  supportPhone={tenant.telefone_suporte}
                  supportEmail={tenant.email_reply_to}
                />
              </div>
            )}

            <form onSubmit={submit} noValidate className="mt-10 flex flex-col gap-5">
              <Field
                id="login-email"
                label="Email"
                icon="mail"
                type="email"
                autocomplete="email"
                placeholder="seu@email.com"
                value={email}
                onChange={setEmail}
              />
              <Field
                id="login-password"
                label="Senha"
                icon="lock"
                type="password"
                autocomplete="current-password"
                value={password}
                onChange={setPassword}
              />

              {failure && (
                <div
                  role="alert"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '10px 12px',
                    background: 'var(--rx-negative-tint)',
                    border: '0.5px solid var(--rx-negative)',
                    borderRadius: 10,
                  }}
                >
                  <Icon name="warn" size={16} color="var(--rx-negative)" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                    <span style={{ color: 'var(--rx-text)' }}>{failure.message}</span>
                    {failure.hint && (
                      <span style={{ color: 'var(--rx-text-2)' }}>{failure.hint}</span>
                    )}
                  </div>
                </div>
              )}
            </form>

            <div className="flex-1" />

            <div style={{ paddingBottom: 28, marginTop: 24 }}>
              <IonButton
                expand="block"
                className="rx-button"
                disabled={!email || !password || loginMutation.isPending}
                onClick={() => loginMutation.mutate()}
              >
                {loginMutation.isPending ? (
                  <IonSpinner name="dots" />
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <Icon name="lock" size={18} color="var(--rx-brand-ink)" />
                    Entrar com email
                  </span>
                )}
              </IonButton>

              <div
                style={{
                  marginTop: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 16,
                }}
              >
                <a
                  href={FORGOT_PASSWORD_URL}
                  rel="noopener"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: 8,
                    color: 'var(--rx-text-2)',
                    fontSize: 13,
                    fontWeight: 500,
                    textDecoration: 'none',
                  }}
                >
                  <Icon name="help" size={14} />
                  Esqueci a senha
                </a>
              </div>

              <div
                style={{
                  marginTop: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  color: 'var(--rx-text-3)',
                }}
              >
                <Icon name="shield" size={12} />
                <span
                  className="rx-mono"
                  style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.2px' }}
                >
                  CRIPTOGRAFIA AES-256 · LGPD
                </span>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}

interface FieldProps {
  id: string;
  label: string;
  icon: 'mail' | 'lock';
  type: 'email' | 'password';
  autocomplete: 'email' | 'current-password';
  placeholder?: string;
  value: string;
  onChange: (next: string) => void;
}

function Field({
  id,
  label,
  icon,
  type,
  autocomplete,
  placeholder,
  value,
  onChange,
}: FieldProps): JSX.Element {
  return (
    <div className="rx-field">
      <label
        htmlFor={id}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--rx-text-3)',
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
        }}
      >
        <Icon name={icon} size={12} />
        {label}
      </label>
      <IonInput
        id={id}
        type={type}
        inputmode={type === 'email' ? 'email' : undefined}
        autocomplete={autocomplete}
        required
        placeholder={placeholder}
        value={value}
        onIonInput={(event) => onChange(event.detail.value ?? '')}
      />
    </div>
  );
}
